<template>
  <Teleport to="body">
    <!-- Trigger Button (always visible when minimized) -->
    <DevPanelTrigger v-if="isMinimized" :active-count="activeScenarioCount" @toggle="togglePanel" />

    <!-- Detached State History Popover -->
    <StateHistoryPopover />

    <!-- Main Panel -->
    <Transition name="panel">
      <div v-if="!isMinimized" class="dev-panel" :class="{ expanded: isExpanded, resizing: isResizingRef }" :style="panelStyle" ref="panelRef">
        <!-- Header -->
        <div class="panel-header" @mousedown="startDrag">
          <div class="header-left">
            <span class="logo">
              <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="18" height="18">
                <path d="M12 2L2 7l10 5 10-5-10-5Z" />
                <path d="m2 17 10 5 10-5" />
                <path d="m2 12 10 5 10-5" />
              </svg>
            </span>
            <span class="title">Dev Panel</span>
            <span class="env-badge">LOCAL</span>
          </div>
          <div class="header-actions">
            <button class="header-btn" @click="toggleExpanded" :title="isExpanded ? 'Collapse' : 'Expand'">
              <svg
                v-if="!isExpanded"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                width="16"
                height="16"
              >
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
              <svg v-else
viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16"
height="16">
                <path d="M4 14h6v6" />
                <path d="M20 10h-6V4" />
                <path d="M14 10l7-7" />
                <path d="M3 21l7-7" />
              </svg>
            </button>
            <button class="header-btn" @click="resetSize" title="Reset Size">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
stroke-width="2" width="16" height="16">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
                <path d="M3 9h18" />
              </svg>
            </button>
            <button class="header-btn" @click="resetPosition" title="Reset Position">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
stroke-width="2" width="16" height="16">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            <button class="header-btn" @click="minimizePanel" title="Minimize (Ctrl+Shift+D)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
stroke-width="2" width="16" height="16">
                <path d="M5 12h14" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="panel-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-btn"
            :class="{ active: activeTab === tab.id }"
            @click="setActiveTab(tab.id)"
            :title="tab.label"
          >
            <component :is="getTabIcon(tab.id)" />
            <span class="tab-label">{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="panel-content">
          <ScenariosTab v-if="activeTab === 'scenarios'" />
          <StateTab v-else-if="activeTab === 'state'" />
          <EventsTab v-else-if="activeTab === 'events'" />
          <StorageTab v-else-if="activeTab === 'storage'" />
          <FeatureFlagsTab v-else-if="activeTab === 'flags'" />
        </div>

        <!-- Footer -->
        <div class="panel-footer">
          <span class="shortcut-hint"> <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>D</kbd> to toggle </span>
          <span class="size-indicator">{{ width }}×{{ height }}</span>
        </div>

        <!-- Resize Handles -->
        <div class="resize-handle resize-n" @mousedown.prevent.stop="handleResizeStart($event, 'n')"></div>
        <div class="resize-handle resize-s" @mousedown.prevent.stop="handleResizeStart($event, 's')"></div>
        <div class="resize-handle resize-e" @mousedown.prevent.stop="handleResizeStart($event, 'e')"></div>
        <div class="resize-handle resize-w" @mousedown.prevent.stop="handleResizeStart($event, 'w')"></div>
        <div class="resize-handle resize-ne" @mousedown.prevent.stop="handleResizeStart($event, 'ne')"></div>
        <div class="resize-handle resize-nw" @mousedown.prevent.stop="handleResizeStart($event, 'nw')"></div>
        <div class="resize-handle resize-se" @mousedown.prevent.stop="handleResizeStart($event, 'se')"></div>
        <div class="resize-handle resize-sw" @mousedown.prevent.stop="handleResizeStart($event, 'sw')"></div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, h } from "vue";
import { useDevPanel, DEV_PANEL_TABS } from "@/composables/useDevPanel";
import { useDevSettings } from "@/composables/useDevSettings";
import DevPanelTrigger from "./DevPanelTrigger.vue";
import ScenariosTab from "./tabs/ScenariosTab.vue";
import StateTab from "./tabs/StateTab.vue";
import EventsTab from "./tabs/EventsTab.vue";
import StorageTab from "./tabs/StorageTab.vue";
import FeatureFlagsTab from "./tabs/FeatureFlagsTab.vue";
import StateHistoryPopover from "./StateHistoryPopover.vue";

const {
  isMinimized,
  isExpanded,
  activeTab,
  position,
  height,
  width,
  togglePanel,
  minimizePanel,
  setActiveTab,
  updatePosition,
  updateHeight,
  updateWidth,
  updateSize,
  resetPosition,
  resetSize,
  toggleExpanded,
} = useDevPanel();

const { settings } = useDevSettings();

const tabs = DEV_PANEL_TABS;
const panelRef = ref<HTMLElement | null>(null);

// Count active scenarios
const activeScenarioCount = computed(() => {
  let count = 0;
  if (settings.value.simulateSocketDisconnect) count++;
  if (settings.value.simulatePollingTimeout) count++;
  return count;
});

// Panel position and size style
const panelStyle = computed(() => {
  const pos = position.value;
  const baseHeight = height.value;
  const baseWidth = width.value;
  // When expanded, increase dimensions
  const h = isExpanded.value ? Math.min(baseHeight * 1.5, 900) : baseHeight;
  const w = isExpanded.value ? Math.min(baseWidth * 1.5, 1000) : baseWidth;

  if (pos.x === -1 || pos.y === -1) {
    // Default position: bottom-left
    return {
      left: "20px",
      bottom: "20px",
      height: `${h}px`,
      width: `${w}px`,
    };
  }

  return {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    height: `${h}px`,
    width: `${w}px`,
  };
});

// Tab icons (inline SVG components)
const getTabIcon = (tabId: string) => {
  const icons: Record<string, any> = {
    scenarios: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", {
            d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z",
          }),
        ],
      ),
    state: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [h("path", { d: "m18 16 4-4-4-4" }), h("path", { d: "m6 8-4 4 4 4" }), h("path", { d: "m14.5 4-5 16" })],
      ),
    events: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }),
          h("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" }),
        ],
      ),
    storage: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("rect", { width: "20", height: "5", x: "2", y: "3", rx: "1" }),
          h("path", { d: "M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" }),
          h("path", { d: "M10 12h4" }),
        ],
      ),
    flags: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", { d: "M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" }),
          h("line", { x1: "4", y1: "22", x2: "4", y2: "15" }),
        ],
      ),
  };
  return icons[tabId] || icons.scenarios;
};

// Dragging logic
let isDragging = false;
let dragStartX = 0;
let dragStartY = 0;
let panelStartX = 0;
let panelStartY = 0;

const startDrag = (e: MouseEvent) => {
  if ((e.target as HTMLElement).closest(".header-btn")) return;

  isDragging = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;

  const panel = panelRef.value;
  if (panel) {
    const rect = panel.getBoundingClientRect();
    panelStartX = rect.left;
    panelStartY = rect.top;
  }

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
};

const onDrag = (e: MouseEvent) => {
  if (!isDragging) return;

  const deltaX = e.clientX - dragStartX;
  const deltaY = e.clientY - dragStartY;

  updatePosition(panelStartX + deltaX, panelStartY + deltaY);
};

const stopDrag = () => {
  isDragging = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
};

// Resize logic
type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
const isResizingRef = ref(false);
let isResizing = false;
let resizeDirection: ResizeDirection | null = null;
let resizeStartX = 0;
let resizeStartY = 0;
let widthStart = 0;
let heightStart = 0;
let posXStart = 0;
let posYStart = 0;

const handleResizeStart = (e: MouseEvent, direction: ResizeDirection) => {
  isResizing = true;
  isResizingRef.value = true;
  resizeDirection = direction;
  resizeStartX = e.clientX;
  resizeStartY = e.clientY;
  widthStart = width.value;
  heightStart = height.value;
  
  const panel = panelRef.value;
  if (panel) {
    const rect = panel.getBoundingClientRect();
    posXStart = rect.left;
    posYStart = rect.top;
  }

  document.addEventListener("mousemove", onResize);
  document.addEventListener("mouseup", stopResize);
  document.body.style.cursor = getCursorForDirection(direction);
  document.body.style.userSelect = "none";
  document.body.style.webkitUserSelect = "none";
};

const getCursorForDirection = (dir: ResizeDirection): string => {
  const cursors: Record<ResizeDirection, string> = {
    n: "ns-resize",
    s: "ns-resize",
    e: "ew-resize",
    w: "ew-resize",
    ne: "nesw-resize",
    sw: "nesw-resize",
    nw: "nwse-resize",
    se: "nwse-resize",
  };
  return cursors[dir];
};

const onResize = (e: MouseEvent) => {
  if (!isResizing || !resizeDirection) return;
  
  e.preventDefault();

  const deltaX = e.clientX - resizeStartX;
  const deltaY = e.clientY - resizeStartY;
  
  let newWidth = widthStart;
  let newHeight = heightStart;
  let newX = posXStart;
  let newY = posYStart;

  // Handle horizontal resizing
  if (resizeDirection.includes("e")) {
    newWidth = widthStart + deltaX;
  } else if (resizeDirection.includes("w")) {
    newWidth = widthStart - deltaX;
    newX = posXStart + deltaX;
  }

  // Handle vertical resizing
  if (resizeDirection.includes("s")) {
    newHeight = heightStart + deltaY;
  } else if (resizeDirection.includes("n")) {
    newHeight = heightStart - deltaY;
    newY = posYStart + deltaY;
  }

  // Apply constraints
  const minWidth = 320;
  const maxWidth = 1200;
  const minHeight = 200;
  const maxHeight = 900;

  // Clamp width and adjust position if resizing from west
  if (newWidth < minWidth) {
    if (resizeDirection.includes("w")) {
      newX = posXStart + (widthStart - minWidth);
    }
    newWidth = minWidth;
  } else if (newWidth > maxWidth) {
    if (resizeDirection.includes("w")) {
      newX = posXStart + (widthStart - maxWidth);
    }
    newWidth = maxWidth;
  }

  // Clamp height and adjust position if resizing from north
  if (newHeight < minHeight) {
    if (resizeDirection.includes("n")) {
      newY = posYStart + (heightStart - minHeight);
    }
    newHeight = minHeight;
  } else if (newHeight > maxHeight) {
    if (resizeDirection.includes("n")) {
      newY = posYStart + (heightStart - maxHeight);
    }
    newHeight = maxHeight;
  }

  // Update size
  updateSize(newWidth, newHeight);
  
  // Update position if resizing from north or west
  if (resizeDirection.includes("n") || resizeDirection.includes("w")) {
    updatePosition(newX, newY);
  }
};

const stopResize = () => {
  isResizing = false;
  isResizingRef.value = false;
  resizeDirection = null;
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  document.body.style.webkitUserSelect = "";
};

// Keyboard shortcuts
const handleKeyDown = (e: KeyboardEvent) => {
  // Ctrl+Shift+D - Toggle panel
  // Use toUpperCase() because e.key varies with Shift key state
  if (e.ctrlKey && e.shiftKey && e.key.toUpperCase() === "D") {
    e.preventDefault();
    togglePanel();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeyDown);
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
});
</script>

<style scoped>
.dev-panel {
  position: fixed;
  min-width: 320px;
  max-width: 1200px;
  min-height: 200px;
  max-height: 900px;
  background: linear-gradient(180deg, #1a1a2e 0%, #16162a 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  display: flex;
  flex-direction: column;
  z-index: 99999999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  /* overflow: hidden is applied to panel-content instead to allow resize handles to extend beyond border */
}

/* Header */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: move;
  user-select: none;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60a5fa;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: #f0f0f8;
  letter-spacing: 0.3px;
}

.env-badge {
  font-size: 9px;
  font-weight: 700;
  color: #10b981;
  background: rgba(16, 185, 129, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.header-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #9090a0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e8e8f0;
}

/* Tabs */
.panel-tabs {
  display: flex;
  padding: 8px 10px;
  gap: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  color: #9090a0;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.tab-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #c0c0d0;
}

.tab-btn.active {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

.tab-label {
  display: none;
}

@media (min-width: 400px) {
  .tab-label {
    display: inline;
  }
}

/* Content */
.panel-content {
  flex: 1;
  min-height: 0; /* Critical for flex child scrolling */
  overflow: hidden; /* Don't scroll here - let child tabs handle it */
  padding: 14px;
  display: flex;
  flex-direction: column;
}


/* Footer */
.panel-footer {
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shortcut-hint {
  font-size: 11px;
  color: #8090a0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.size-indicator {
  font-size: 10px;
  font-family: "JetBrains Mono", "Fira Code", "SF Mono", Monaco, Consolas, monospace;
  color: #606080;
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
}

.shortcut-hint :deep(kbd),
.shortcut-hint kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  min-width: 22px;
  font-size: 10px;
  font-family: "SF Mono", "Consolas", monospace;
  font-weight: 500;
  color: #c0c8d8;
  background: linear-gradient(180deg, #2a2a3a 0%, #1e1e2e 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

/* Resize Handles */
.resize-handle {
  position: absolute;
  background: transparent;
  z-index: 100;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
  pointer-events: auto;
}

/* Prevent text selection during resize */
.dev-panel.resizing,
.dev-panel.resizing * {
  user-select: none !important;
  -webkit-user-select: none !important;
  cursor: inherit !important;
}

/* Edge handles */
.resize-n {
  top: 0;
  left: 14px;
  right: 14px;
  height: 6px;
  cursor: ns-resize;
}

.resize-s {
  bottom: 0;
  left: 14px;
  right: 14px;
  height: 6px;
  cursor: ns-resize;
}

.resize-e {
  top: 14px;
  right: 0;
  bottom: 14px;
  width: 6px;
  cursor: ew-resize;
}

.resize-w {
  top: 14px;
  left: 0;
  bottom: 14px;
  width: 6px;
  cursor: ew-resize;
}

/* Corner handles */
.resize-ne {
  top: 0;
  right: 0;
  width: 14px;
  height: 14px;
  cursor: nesw-resize;
}

.resize-nw {
  top: 0;
  left: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
}

.resize-se {
  bottom: 0;
  right: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
}

.resize-sw {
  bottom: 0;
  left: 0;
  width: 14px;
  height: 14px;
  cursor: nesw-resize;
}

/* Hover effects for edges */
.resize-n:hover,
.resize-s:hover {
  background: rgba(59, 130, 246, 0.3);
}

.resize-e:hover,
.resize-w:hover {
  background: rgba(59, 130, 246, 0.3);
}

/* Hover effects for corners */
.resize-ne:hover,
.resize-nw:hover,
.resize-se:hover,
.resize-sw:hover {
  background: rgba(59, 130, 246, 0.4);
}

/* Transitions */
.panel-enter-active,
.panel-leave-active {
  transition: all 0.2s ease;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
</style>

